/**
* RequestHandler.ts
* @author Hector Hernandez (hectorh)
* @copyright Microsoft 2019
*/
import { DisabledPropertyName, isReactNative, isXhrSupported, openXhr, useXDomainRequest } from "@microsoft/applicationinsights-core-js";
function _getResponseText(xhr) {
    try {
        return xhr.responseText;
    }
    catch (e) {
        // Best effort, as XHR may throw while XDR wont so just ignore
    }
    return "";
}
var RequestHandler = /** @class */ (function () {
    function RequestHandler() {
    }
    /**
    * Make backend request
    */
    RequestHandler.prototype.makeRequest = function (urlString, data, method, oncomplete) {
        var _a;
        var _this = this;
        if (useXDomainRequest()) {
            // It doesn't support custom headers, so no action is taken with current requestHeaders
            var xdr_1 = new XDomainRequest();
            xdr_1.open(method, urlString);
            // can't get the status code in xdr.
            xdr_1.onload = function () {
                // we will assume onload means the request succeeded.
                oncomplete(200, _getResponseText(xdr_1), {});
            };
            xdr_1.onerror = function () {
                // we will assume onerror means we need to drop the events.
                oncomplete(400, _getResponseText(xdr_1), {});
            };
            xdr_1.ontimeout = function () {
                // we will assume ontimeout means we need to retry the events.
                oncomplete(500, _getResponseText(xdr_1), {});
            };
            // https://cypressnorth.com/web-programming-and-development/internet-explorer-aborting-ajax-requests-fixed/
            // tslint:disable-next-line:no-empty
            xdr_1.onprogress = function () { };
            setTimeout(function () {
                xdr_1.send(data);
            }, 0);
        }
        else if (isReactNative()) {
            // Use the fetch API to send events in React Native
            fetch(urlString, (_a = {
                    body: data,
                    method: method,
                    credentials: "include"
                },
                _a[DisabledPropertyName] = true,
                _a)).then(function (response) {
                var headerMap = {};
                if (response.headers) {
                    response.headers.forEach(function (value, name) {
                        headerMap[name] = value;
                    });
                }
                oncomplete(response.status, JSON.stringify(response.json()), headerMap);
            }).catch(function (error) {
                // In case there is an error in the request. Set the status to 0
                oncomplete(0, error, {});
            });
        }
        else if (isXhrSupported()) {
            var xhr_1 = openXhr(method, urlString, true, true);
            xhr_1.onload = function () {
                oncomplete(xhr_1.status, _getResponseText(xhr_1), _this._convertAllHeadersToMap(xhr_1.getAllResponseHeaders()));
            };
            xhr_1.onerror = function () {
                oncomplete(xhr_1.status, _getResponseText(xhr_1), _this._convertAllHeadersToMap(xhr_1.getAllResponseHeaders()));
            };
            xhr_1.ontimeout = function () {
                oncomplete(xhr_1.status, _getResponseText(xhr_1), _this._convertAllHeadersToMap(xhr_1.getAllResponseHeaders()));
            };
            xhr_1.send(data);
        }
    };
    /**
    * Converts the XHR getAllResponseHeaders to a map containing the header key and value.
    */
    RequestHandler.prototype._convertAllHeadersToMap = function (headersString) {
        var headers = {};
        if (headersString) {
            var headersArray = headersString.split("\n");
            for (var i = 0; i < headersArray.length; ++i) {
                var header = headersArray[i].split(": ");
                headers[header[0]] = header[1];
            }
        }
        return headers;
    };
    return RequestHandler;
}());
export { RequestHandler };
//# sourceMappingURL=RequestHandler.js.map